/** Browser speech starts only on a button press and belongs to one QuantisAI conversation. */
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useSIInputLevel} from './useSIInputLevel';

interface RecognitionResult {isFinal: boolean; readonly [index: number]: {transcript: string};}
interface RecognitionEvent {resultIndex: number; results: {length: number; readonly [index: number]: RecognitionResult};}
export interface SIWebRecognition {
  lang: string; continuous: boolean; interimResults: boolean; maxAlternatives: number;
  onstart: (() => void) | null; onend: (() => void) | null;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: {error: string}) => void) | null;
  start(): void | Promise<void>; stop(): void; abort(): void;
}
interface SpeechWindow {
  SpeechRecognition?: new () => SIWebRecognition;
  webkitSpeechRecognition?: new () => SIWebRecognition;
  speechSynthesis?: SpeechSynthesis;
  SpeechSynthesisUtterance?: typeof SpeechSynthesisUtterance;
}
export interface SIWebSpeechOptions {
  contextKey: string;
  draft: string;
  onDraftChange: (text: string) => void;
  onVoiceSubmit: (text: string) => boolean | void | Promise<boolean | void>;
  disabled?: boolean;
  voiceDisabled?: boolean;
  pending?: boolean;
  failed?: boolean;
  latestReply?: {id: string; text: string} | null;
  maxLength?: number;
}
interface SpeechState {listening: boolean; voiceMode: boolean; speaking: boolean; awaitingReply: boolean; transcript: string; error: string | null;}
interface Capture {
  recognition: SIWebRecognition; mode: 'dictation' | 'voice'; context: string;
  baseDraft: string; expectedDraft: string; finals: Map<number, string>; failed: boolean;
}
interface VoiceTurn {context: string; baselineReplyId?: string; submitted: boolean; sawPending: boolean;}
const idle: SpeechState = {listening: false, voiceMode: false, speaking: false, awaitingReply: false, transcript: '', error: null};
const browserSpeech = (): SpeechWindow => typeof window === 'undefined' ? {} : window as unknown as SpeechWindow;
const recognitionError = (code: string) => ({
  'not-allowed': 'Microphone access was denied. Allow microphone access in your browser and try again.',
  'service-not-allowed': 'Your browser does not allow speech recognition here.',
  'audio-capture': 'No microphone is available. Check your microphone and try again.',
  'no-speech': 'No speech was heard. Press the microphone and try again.',
  network: 'The browser speech service could not connect. Please try again.',
  'language-not-supported': 'English speech recognition is unavailable in this browser.',
}[code] ?? 'Speech recognition stopped. Please try again.');
const failureMessage = (value: unknown, fallback: string) => value instanceof Error && value.message ? value.message : fallback;
export function spokenReplyText(text: string): string {
  const prefix = '```quantis-question\n';
  if (text.startsWith(prefix) && text.endsWith('\n```')) {
    try {
      const value: unknown = JSON.parse(text.slice(prefix.length, -4));
      if (value && typeof value === 'object' && 'question' in value && 'options' in value
        && typeof value.question === 'string' && value.question.trim() && value.question.length <= 400
        && Array.isArray(value.options) && value.options.length >= 2 && value.options.length <= 4
        && value.options.every((option: unknown) => typeof option === 'string' && option.trim() && option.length <= 200)) {
        text = `${value.question} Your options are: ${value.options.join('; ')}.`;
      }
    } catch { /* Invalid tool payloads remain excluded as fenced code. */ }
  }
  return text
  .replace(/```[\s\S]*?```/g, ' ')
  .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
  .replace(/https?:\/\/\S+/g, ' ')
  .replace(/^[ \t]*(?:#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/gm, '')
  .replace(/[`*_~]/g, '')
  .replace(/\s+/g, ' ').trim();
}
const append = (draft: string, transcript: string) => `${draft}${draft && !/\s$/.test(draft) && transcript ? ' ' : ''}${transcript}`;

export function useSIWebSpeech(options: SIWebSpeechOptions) {
  const {inputLevel, start: startMeter, stop: stopMeter} = useSIInputLevel({contextKey: options.contextKey});
  const disabled = Boolean(options.disabled);
  const available = browserSpeech();
  const Recognition = available.SpeechRecognition ?? available.webkitSpeechRecognition;
  const dictationSupported = Boolean(Recognition);
  const voiceSupported = dictationSupported && Boolean(available.speechSynthesis && available.SpeechSynthesisUtterance);
  const latest = useRef(options);
  useLayoutEffect(() => {latest.current = options;});
  const mounted = useRef(false);
  const capture = useRef<Capture | null>(null);
  const voiceTurn = useRef<VoiceTurn | null>(null);
  const utterance = useRef<SpeechSynthesisUtterance | null>(null);
  const [state, setState] = useState<SpeechState>(idle);
  const publish = useCallback((patch: Partial<SpeechState>) => {if (mounted.current) setState(current => ({...current, ...patch}));}, []);
  const cancelSpeech = useCallback(() => {
    const own = utterance.current;
    utterance.current = null;
    if (own) {
      own.onend = null; own.onerror = null;
      browserSpeech().speechSynthesis?.cancel();
    }
  }, []);
  const abortCapture = useCallback(() => {
    const active = capture.current;
    capture.current = null;
    if (!active) return;
    active.recognition.onstart = null; active.recognition.onend = null;
    active.recognition.onresult = null; active.recognition.onerror = null;
    try {active.recognition.abort();} catch { /* A browser may have already ended the capture. */ }
  }, []);
  const stop = useCallback(() => {
    abortCapture(); stopMeter(); cancelSpeech(); voiceTurn.current = null;
    publish({listening: false, voiceMode: false, speaking: false, awaitingReply: false});
  }, [abortCapture, stopMeter, cancelSpeech, publish]);
  useLayoutEffect(() => {
    mounted.current = true;
    stop();
    publish({transcript: '', error: null});
    return () => {mounted.current = false; stop();};
  }, [options.contextKey, disabled, stop, publish]);
  useLayoutEffect(() => {
    const active = capture.current;
    if (active?.mode === 'dictation' && options.draft !== active.expectedDraft) stop();
    if (active?.mode === 'voice' && (options.voiceDisabled || options.pending || options.draft.trim())) stop();
  }, [options.draft, options.voiceDisabled, options.pending, stop]);

  useEffect(() => {
    const hide = () => {if (document.visibilityState === 'hidden') stop();};
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('pagehide', stop);
    return () => {document.removeEventListener('visibilitychange', hide); window.removeEventListener('pagehide', stop);};
  }, [stop]);

  const start = useCallback((mode: Capture['mode']) => {
    const current = latest.current;
    if (current.disabled) return;
    const browser = browserSpeech();
    const Constructor = browser.SpeechRecognition ?? browser.webkitSpeechRecognition;
    if (!Constructor || (mode === 'voice' && (!browser.speechSynthesis || !browser.SpeechSynthesisUtterance))) {
      publish({error: mode === 'voice' ? 'Voice conversation is not supported by this browser. Try dictation or type your request.' : 'Dictation is not supported by this browser. Type your request instead.'});
      return;
    }
    if (mode === 'voice' && (current.voiceDisabled || current.pending || current.draft.trim())) {
      publish({error: 'Send or clear your draft and attachments, then start a voice request when QuantisAI is ready.'});
      return;
    }
    stop();
    const maxLength = Math.max(1, current.maxLength ?? 4000);
    let recognition: SIWebRecognition;
    try {recognition = new Constructor();} catch (error) {
      publish({error: failureMessage(error, 'Your browser could not start speech recognition.')}); return;
    }
    const active: Capture = {recognition, mode, context: current.contextKey, baseDraft: current.draft, expectedDraft: current.draft, finals: new Map(), failed: false};
    capture.current = active;
    const isCurrent = () => mounted.current && capture.current === active && latest.current.contextKey === active.context && !latest.current.disabled;
    recognition.lang = 'en-GB'; recognition.continuous = mode === 'dictation'; recognition.interimResults = true; recognition.maxAlternatives = 1;
    publish({listening: true, voiceMode: mode === 'voice', speaking: false, awaitingReply: false, transcript: '', error: null});
    recognition.onstart = () => {if (isCurrent()) publish({listening: true});};
    recognition.onresult = event => {
      if (!isCurrent()) return;
      const interim: string[] = [];
      for (let index = 0; index < event.results.length; index++) {
        const result = event.results[index];
        const text = result[0]?.transcript?.trim() ?? '';
        if (result.isFinal) active.finals.set(index, text);
        else interim.push(text);
      }
      const finalText = [...active.finals].sort(([a], [b]) => a - b).map(([, text]) => text).filter(Boolean).join(' ');
      publish({transcript: [finalText, ...interim].filter(Boolean).join(' ').slice(0, maxLength)});
      if (mode === 'dictation' && finalText) {
        const text = append(active.baseDraft, finalText);
        active.expectedDraft = text.slice(0, maxLength);
        latest.current.onDraftChange(active.expectedDraft);
        if (text.length > maxLength) {stop(); publish({error: `Your draft has reached the ${maxLength}-character limit.`});}
      }
    };
    recognition.onerror = event => {
      if (!isCurrent()) return;
      active.failed = true;
      stop();
      if (event.error !== 'aborted') publish({error: recognitionError(event.error)});
    };
    recognition.onend = () => {
      if (!isCurrent()) return;
      capture.current = null;
      stopMeter();
      publish({listening: false});
      if (active.failed || mode !== 'voice') return;
      const text = [...active.finals].sort(([a], [b]) => a - b).map(([, part]) => part).filter(Boolean).join(' ').trim();
      if (!text) {publish({error: recognitionError('no-speech')}); return;}
      if (text.length > maxLength) {publish({error: `Your voice request exceeds the ${maxLength}-character limit. Please shorten it and try again.`}); return;}
      // Only the spoken turn is submitted. Existing typed content is never included.
      if (latest.current.voiceDisabled || latest.current.pending || latest.current.draft.trim()) {stop(); publish({error: 'Your draft changed. Review it before starting another voice request.'}); return;}
      const turn: VoiceTurn = {context: active.context, baselineReplyId: latest.current.latestReply?.id, submitted: false, sawPending: false};
      voiceTurn.current = turn;
      publish({awaitingReply: true, error: null, transcript: text});
      void Promise.resolve().then(() => {
        if (voiceTurn.current !== turn || latest.current.contextKey !== turn.context || latest.current.disabled) return false;
        return latest.current.onVoiceSubmit(text);
      }).then(result => {
        if (voiceTurn.current !== turn || !mounted.current) return;
        if (result === false) {voiceTurn.current = null; publish({awaitingReply: false, error: 'QuantisAI could not answer. Your spoken request has been kept.'});}
        else {turn.submitted = true; publish({});}
      }).catch(error => {
        if (voiceTurn.current !== turn || !mounted.current) return;
        voiceTurn.current = null;
        publish({awaitingReply: false, error: failureMessage(error, 'QuantisAI could not answer. Your spoken request has been kept.')});
      });
    };
    try {
      void Promise.resolve(recognition.start()).catch(error => {
        if (!isCurrent()) return;
        stop(); publish({error: failureMessage(error, 'Your browser could not start speech recognition.')});
      });
      if (isCurrent()) startMeter();
    } catch (error) {if (isCurrent()) {stop(); publish({error: failureMessage(error, 'Your browser could not start speech recognition.')});}}
  }, [publish, stop, startMeter, stopMeter]);

  const finishCapture = useCallback(() => {
    const active = capture.current;
    if (!active) return;
    try {active.recognition.stop();} catch (error) {stop(); publish({error: failureMessage(error, 'Speech recognition could not finish. Please try again.')});}
  }, [publish, stop]);
  const toggleDictation = useCallback(() => {
    if (capture.current?.mode === 'dictation') finishCapture();
    else start('dictation');
  }, [finishCapture, start]);
  const toggleVoice = useCallback(() => {
    if (capture.current?.mode === 'voice') finishCapture();
    else if (voiceTurn.current || utterance.current) stop();
    else start('voice');
  }, [finishCapture, start, stop]);

  useEffect(() => {
    const turn = voiceTurn.current;
    if (!turn || turn.context !== options.contextKey || options.disabled) return;
    if (options.failed && (turn.sawPending || turn.submitted)) {voiceTurn.current = null; publish({awaitingReply: false, error: 'QuantisAI could not answer. Your spoken request has been kept.'}); return;}
    if (options.pending) {turn.sawPending = true; return;}
    if (!turn.submitted) return;
    const reply = options.latestReply;
    if (!reply || reply.id === turn.baselineReplyId || !reply.text.trim()) {
      if (turn.sawPending) {voiceTurn.current = null; publish({awaitingReply: false});}
      return;
    }
    voiceTurn.current = null;
    const browser = browserSpeech();
    if (!browser.speechSynthesis || !browser.SpeechSynthesisUtterance) {publish({awaitingReply: false, error: 'Spoken replies are unavailable. Read the reply in this chat.'}); return;}
    try {
      const text = spokenReplyText(reply.text);
      if (!text) {publish({awaitingReply: false, speaking: false}); return;}
      const speech = new browser.SpeechSynthesisUtterance(text);
      const localVoices = browser.speechSynthesis.getVoices().filter(voice => voice.localService);
      const localVoice = localVoices.find(voice => voice.lang.toLowerCase() === 'en-gb') ?? localVoices.find(voice => voice.lang.toLowerCase().startsWith('en'));
      if (localVoice) speech.voice = localVoice;
      speech.lang = 'en-GB';
      utterance.current = speech;
      const complete = () => {if (mounted.current && utterance.current === speech) {utterance.current = null; publish({speaking: false});}};
      speech.onend = complete;
      speech.onerror = () => {if (utterance.current === speech) {complete(); publish({error: 'The browser could not play this reply. You can read it in the chat.'});}};
      publish({awaitingReply: false, speaking: true});
      browser.speechSynthesis.speak(speech);
    } catch (error) {utterance.current = null; publish({awaitingReply: false, speaking: false, error: failureMessage(error, 'The browser could not play this reply.')});}
  }, [options.contextKey, options.disabled, options.pending, options.failed, options.latestReply?.id, options.latestReply?.text, state, publish]);

  return {dictationSupported, voiceSupported, inputLevel, ...state, toggleDictation, toggleVoice, stop};
}
